import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TIME_SLOTS } from '../../../utils/studyPlanMockData';
import { Commitment } from '../../../types/study-plan';

interface CommitmentsStepProps {
  onNext: (data: { commitments: Commitment[] }) => void;
  onBack: () => void;
  defaultValue?: { commitments: Commitment[] };
}

const CommitmentsStep: React.FC<CommitmentsStepProps> = ({ onNext, onBack, defaultValue }) => {
  const [commitments, setCommitments] = useState<Commitment[]>(
    defaultValue?.commitments || []
  );

  const addCommitment = (type: 'coaching' | 'games' | 'others') => {
    const existing = commitments.find(c => c.type === type);
    if (!existing) {
      setCommitments(prev => [...prev, { type, timeSlots: [] }]);
    }
  };

  const removeCommitment = (type: 'coaching' | 'games' | 'others') => {
    setCommitments(prev => prev.filter(c => c.type !== type));
  };

  const toggleTimeSlot = (type: 'coaching' | 'games' | 'others', slotId: string) => {
    setCommitments(prev => prev.map(commitment => {
      if (commitment.type === type) {
        const timeSlots = commitment.timeSlots.includes(slotId)
          ? commitment.timeSlots.filter(id => id !== slotId)
          : [...commitment.timeSlots, slotId];
        return { ...commitment, timeSlots };
      }
      return commitment;
    }));
  };

  const updateCustomDescription = (type: 'others', description: string) => {
    setCommitments(prev => prev.map(commitment => {
      if (commitment.type === type) {
        return { ...commitment, customDescription: description };
      }
      return commitment;
    }));
  };

  const getCommitment = (type: 'coaching' | 'games' | 'others') => {
    return commitments.find(c => c.type === type);
  };

  const getCommitmentIcon = (type: string) => {
    switch (type) {
      case 'coaching': return 'fa-chalkboard-teacher';
      case 'games': return 'fa-gamepad';
      case 'others': return 'fa-ellipsis-h';
      default: return 'fa-calendar';
    }
  };

  const getCommitmentColor = (type: string) => {
    switch (type) {
      case 'coaching': return 'info';     // Blue for Coaching Classes
      case 'games': return 'success';     // Green for Sports & Games
      case 'others': return 'warning';    // Orange/Yellow for Other Activities
      default: return 'info';
    }
  };

  const handleSubmit = () => {
    // Validate that each commitment has at least one time slot selected
    const invalidCommitments = commitments.filter(c => c.timeSlots.length === 0);
    if (invalidCommitments.length > 0) {
      alert('Please select time slots for all your commitments');
      return;
    }

    onNext({ commitments });
  };

  const commitmentTypes = [
    { id: 'coaching', label: 'Coaching Classes', description: 'Regular coaching or tuition classes' },
    { id: 'games', label: 'Sports & Games', description: 'Physical activities and sports' },
    { id: 'others', label: 'Other Activities', description: 'Any other regular commitments' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="step-content"
    >
      <div className="container-fluid py-2 py-md-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="text-center mb-5">
              <h2 className="display-6 fw-bold text-info mb-3">
                Add Your Commitments
              </h2>
              <p className="lead text-muted">
                Let us know about your other activities so we can plan around them
              </p>
            </div>

            {/* Commitment Type Selection */}
            <div className="row g-3 g-md-4 mb-4 mb-md-5">
              {commitmentTypes.map((type) => {
                const isSelected = getCommitment(type.id as any);
                return (
                  <div key={type.id} className="col-12 col-sm-6 col-md-4">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`card border-2 cursor-pointer h-100 ${
                        isSelected 
                          ? `border-${getCommitmentColor(type.id)} bg-${getCommitmentColor(type.id)} bg-opacity-10` 
                          : 'border-light hover-shadow'
                      }`}
                      onClick={() => isSelected ? removeCommitment(type.id as any) : addCommitment(type.id as any)}
                    >
                      <div className="card-body text-center p-4">
                        <div className="mb-3">
                          <div className={`rounded-circle mx-auto d-flex align-items-center justify-content-center ${
                            isSelected 
                              ? `bg-white text-dark` 
                              : `bg-${getCommitmentColor(type.id)} bg-opacity-20 text-white`
                          }`} style={{ width: '60px', height: '60px' }}>
                            <i className={`fas ${getCommitmentIcon(type.id)} fa-lg`}></i>
                          </div>
                        </div>
                        
                        <h5 className={`card-title mb-2 fw-bold ${
                          isSelected ? `text-white` : 'text-dark'
                        }`}>
                          {type.label}
                        </h5>
                        
                        <p className="card-text text-dark small mb-3">
                          {type.description}
                        </p>
                        
                        <div className={`badge bg-${getCommitmentColor(type.id)} ${isSelected ? 'text-white' : `bg-opacity-20 text-white`}`}>
                          {isSelected ? (
                            <>
                              <i className="fas fa-check me-1"></i>
                              Added
                            </>
                          ) : (
                            'Add'
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>

            {/* Time Slot Selection for Each Commitment */}
            {commitments.map((commitment) => (
              <motion.div
                key={commitment.type}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="card mb-4 border-light border-2"
              >
                <div className="card-header bg-white border-bottom">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className={`mb-0 text-${getCommitmentColor(commitment.type)}`}>
                      <i className={`fas ${getCommitmentIcon(commitment.type)} me-2 text-${getCommitmentColor(commitment.type)}`}></i>
                      {commitmentTypes.find(t => t.id === commitment.type)?.label}
                    </h5>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeCommitment(commitment.type)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  {commitment.type === 'others' && (
                    <div className="mb-4">
                      <label className="form-label fw-semibold">Describe your activity:</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Music lessons, Art classes, etc."
                        value={commitment.customDescription || ''}
                        onChange={(e) => updateCustomDescription('others', e.target.value)}
                      />
                    </div>
                  )}
                  
                  <h6 className={`text-${getCommitmentColor(commitment.type)} fw-bold mb-3`}>
                    <i className="fas fa-clock me-2"></i>
                    Select Time Slots:
                  </h6>
                  <div className="row g-2">
                    {TIME_SLOTS.map((slot) => (
                      <div key={slot.id} className="col-6 col-sm-4 col-md-4 col-lg-3">
                        <div
                          className={`card border cursor-pointer ${
                            commitment.timeSlots.includes(slot.id)
                              ? `border-${getCommitmentColor(commitment.type)} bg-${getCommitmentColor(commitment.type)} text-white`
                              : 'border-light hover-shadow'
                          }`}
                          onClick={() => toggleTimeSlot(commitment.type, slot.id)}
                        >
                          <div className="card-body p-3 text-center">
                            <h6 className={`mb-1 ${
                              commitment.timeSlots.includes(slot.id) 
                                ? 'text-white' 
                                : 'text-dark'
                            }`}>
                              {slot.label}
                            </h6>
                            <small className={`${
                              commitment.timeSlots.includes(slot.id) 
                                ? 'text-white opacity-75' 
                                : 'text-muted'
                            }`}>
                              {slot.startTime} - {slot.endTime}
                            </small>
                            {commitment.timeSlots.includes(slot.id) && (
                              <div className="position-absolute top-0 end-0 m-2 m-2">
                                <i className="fas fa-check-circle text-white"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {commitment.timeSlots.length === 0 && (
                    <div className="alert alert-warning mt-3" role="alert">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Please select at least one time slot for this activity
                    </div>
                  )}
                </div>
              </motion.div>
            ))}

            {commitments.length === 0 && (
              <div className="text-center py-5">
                <div className="text-muted">
                  <i className="fas fa-info-circle fa-2x mb-3"></i>
                  <p>No commitments added yet. Add your regular activities above to create a personalized schedule.</p>
                </div>
              </div>
            )}

            <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
              <button
                type="button"
                onClick={onBack}
                className="btn btn-outline-secondary btn-lg px-4 order-2 order-md-1"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </button>
              
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-success btn-lg px-5 order-1 order-md-2"
              >
                Generate Study Plan
                <i className="fas fa-magic ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CommitmentsStep;